import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import websiteService from '../../services/websiteService';
import websitePageSpeedService from '../../services/WebsitePageSpeedService';
import { useAuth } from '../../context/AuthContext';
import { useMetaPixel } from '../../hooks/use-meta-pixel';
import { useGoogleAnalytics } from '../../hooks/use-google-analytics';
import { useGoogleAds } from '../../hooks/use-google-ads';
import Toast from '../../components/Toast';
import { renderPlatformOptions } from '../../data/platforms';

const AddWebsite = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trackWebsiteAdded } = useMetaPixel();
  const { trackWebsiteAdded: trackGAWebsiteAdded } = useGoogleAnalytics();
  const { trackWebsiteAdded: trackAdsWebsiteAdded } = useGoogleAds();
  const [formData, setFormData] = useState({
    name: '',
    root_url: '',
    platform: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isCheckingUrl, setIsCheckingUrl] = useState(false);
  const [urlDebounceTimer, setUrlDebounceTimer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [toast, setToast] = useState({ visible: false, message: "", type: "" });
  const [pageSpeedAnalysis, setPageSpeedAnalysis] = useState({
    isRunning: false,
    progress: 0,
    status: 'starting',
    message: 'Initializing...',
    websiteId: null
  });

  // Function to validate and check if URL already exists
  const isValidUrl = async (urlString, checkExisting = true) => {
    try {
      let url = urlString.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      const parsedUrl = new URL(url);
      
      if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
        return { isValid: false, message: 'URL must use either HTTP or HTTPS protocol' };
      }
      
      if (!parsedUrl.hostname.includes('.') || 
          parsedUrl.hostname.includes(' ') || 
          !parsedUrl.hostname.match(/^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/)) {
        return { isValid: false, message: 'Please enter a valid domain name' };
      }
      
      const tld = parsedUrl.hostname.split('.').pop();
      if (tld.length < 2) {
        return { isValid: false, message: 'Domain has an invalid TLD (top-level domain)' };
      }
      
      if (parsedUrl.pathname !== '/' && parsedUrl.pathname !== '') {
        return { isValid: false, message: 'Please enter only the root domain without any pages or paths' };
      }
      
      if (parsedUrl.search !== '') {
        return { isValid: false, message: 'Please enter only the root domain without any query parameters' };
      }
      
      const rootUrl = `${parsedUrl.protocol}//${parsedUrl.hostname}`;
      
      if (checkExisting) {
        try {
          setIsCheckingUrl(true);
          const response = await websiteService.getWebsiteByUrl(rootUrl);
          setIsCheckingUrl(false);
          
          if (response && response.website) {
            return { 
              isValid: false, 
              message: 'You already have a website with this URL. Please use a different URL.',
              formattedUrl: rootUrl 
            };
          }
        } catch (error) {
          setIsCheckingUrl(false);
          
          if (!error.response || error.response.status !== 404) {
            console.error('Error checking website URL existence:', error);
          }
        }
      }
      
      return { isValid: true, formattedUrl: rootUrl };
    } catch (e) {
      return { isValid: false, message: 'Please enter a valid URL' };
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (isSubmitting) return;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    if (name === 'root_url' && value.trim() !== '') {
      if (urlDebounceTimer) {
        clearTimeout(urlDebounceTimer);
      }
      
      const timer = setTimeout(async () => {
        const urlValidation = await isValidUrl(value);
        if (urlValidation.isValid) {
          setFormData(prev => ({
            ...prev,
            root_url: urlValidation.formattedUrl
          }));
        } else if (urlValidation.message.includes('already have a website')) {
          setErrors(prev => ({
            ...prev,
            root_url: urlValidation.message
          }));
        }
      }, 800);
      
      setUrlDebounceTimer(timer);
    }
  };

  // Validate form before submission
  const validateForm = async () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Website name is required';
    }
    
    if (!formData.root_url.trim()) {
      newErrors.root_url = 'Website URL is required';
    } else {
      const urlValidation = await isValidUrl(formData.root_url);
      if (!urlValidation.isValid) {
        newErrors.root_url = urlValidation.message;
      } else {
        setFormData(prev => ({
          ...prev,
          root_url: urlValidation.formattedUrl
        }));
      }
    }
    
    if (!formData.platform.trim()) {
      newErrors.platform = 'Platform is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (await validateForm()) {
      handleSaveWebsite(formData);
    }
  };

  // Clean up effect to clear timeout when component unmounts
  useEffect(() => {
    return () => {
      if (urlDebounceTimer) {
        clearTimeout(urlDebounceTimer);
      }
    };
  }, [urlDebounceTimer]);

  // Handle website creation
  const handleSaveWebsite = async (websiteData) => {
    setIsSubmitting(true);
    setProcessingStatus('Submitting...');
    
    try {
      const dataToSubmit = {
        ...websiteData,
        user_id: user.id
      };
      
      setProcessingStatus('Creating website...');
      const response = await websiteService.createWebsite(dataToSubmit);
      
      setToast({
        visible: true,
        message: "Website added. Processing and analyzing website...",
        type: "info"
      });
      
      try {
        const website = response.website || response;
        
        setProcessingStatus('Crawling website content...');
        const processedWebsiteResponse = await websiteService.waitForCrawlingComplete(website.id);
        
        const processedWebsite = processedWebsiteResponse.website || processedWebsiteResponse;
        
        console.log("Crawling complete:", processedWebsite);
        
        await runPageSpeedAnalysis(processedWebsite);
        
      } catch (crawlingError) {
        console.error("Error during website crawling:", crawlingError);
        
        const website = response.website || response;
        
        await runPageSpeedAnalysis(website);
      }
    } catch (error) {
      console.error("Error creating website:", error);
      setToast({
        visible: true,
        message: "Failed to create website. Please try again.",
        type: "error"
      });
      setIsSubmitting(false);
      setProcessingStatus('');
    }
  };
  
  // Run PageSpeed analysis
  const runPageSpeedAnalysis = async (website) => {
    if (!website || !website.root_url) {
      console.error("Cannot run PageSpeed analysis - invalid website data");
      return;
    }
    
    try {
      setProcessingStatus('Starting PageSpeed analysis...');
      setPageSpeedAnalysis({
        isRunning: true,
        progress: 0,
        status: 'starting',
        message: 'Initializing PageSpeed analysis...',
        websiteId: website.id
      });
      
      await websitePageSpeedService.analyzeWebsite(website, {
        mobile: true,
        desktop: true,
        onProgress: (progress) => {
          if (progress.status === 'analyzing-desktop') {
            setProcessingStatus('Analyzing desktop performance...');
          } else if (progress.status === 'analyzing-mobile') {
            setProcessingStatus('Analyzing mobile performance...');
          } else if (progress.status === 'complete') {
            setProcessingStatus('Analysis complete!');
          } else if (progress.status === 'error') {
            setProcessingStatus('Analysis failed');
          }
          
          setPageSpeedAnalysis({
            isRunning: progress.status !== 'complete' && progress.status !== 'error',
            progress: progress.progress,
            status: progress.status,
            message: progress.message,
            websiteId: website.id
          });
        }
      });
      
      setToast({
        visible: true,
        message: "Website added successfully! PageSpeed analysis complete.",
        type: "success"
      });
      
      // Track website addition
      trackWebsiteAdded();

      // Track website addition with Google Analytics
      trackGAWebsiteAdded();

      // Track website addition with Google Ads
      trackAdsWebsiteAdded({
        platform: formData.platform,
        value: 15, // Assign value to website addition conversion
        currency: 'USD'
      });
      
      setTimeout(() => {
        navigate("/dashboard/websites");
      }, 1500);
      
    } catch (error) {
      console.error("Error during PageSpeed analysis:", error);
      
      setPageSpeedAnalysis(prev => ({
        ...prev,
        isRunning: false,
        status: 'error',
        message: error.message || 'Error running PageSpeed analysis'
      }));
      
      setToast({
        visible: true,
        message: "Website added, but PageSpeed analysis failed. You can retry it from website details page.",
        type: "warning"
      });
      
      setTimeout(() => {
        navigate("/dashboard/websites");
      }, 1500);
    }
  };

  // Close toast notification
  const closeToast = () => {
    setToast({ ...toast, visible: false });
  };

  const disabledStyle = isSubmitting ? 
    'bg-gray-100 cursor-not-allowed opacity-75' : 
    'bg-gray-50';
    
  const getSubmitButtonText = () => {
    if (!isSubmitting) return "Add Website";
    
    if (processingStatus) {
      return processingStatus;
    }
    
    if (pageSpeedAnalysis.isRunning) {
      return "Analyzing...";
    }
    
    return "Creating...";
  };

  return (
    <div className="container max-w-3xl mx-auto p-4">
      {toast.visible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
          duration={5000}
        />
      )}

      <div className="mb-6">
        <Link 
          to="/dashboard/websites" 
          className={`flex items-center text-blue-600 hover:text-blue-800 ${isSubmitting ? 'pointer-events-none opacity-50' : ''}`}
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to websites
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <h1 className="text-xl font-bold">Add New Website</h1>
          <p className="text-sm text-gray-500 mt-1">
            Add a website to monitor its performance and receive optimization recommendations.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-5">
          <div className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Website Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`${disabledStyle} border ${errors.name ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                placeholder="My Website"
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            
            <div>
              <label htmlFor="root_url" className="block text-sm font-medium text-gray-700 mb-1">
                Website URL *
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="root_url"
                  name="root_url"
                  value={formData.root_url}
                  onChange={handleChange}
                  disabled={isSubmitting || isCheckingUrl}
                  className={`${disabledStyle} border ${errors.root_url ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-8`}
                  placeholder="https://example.com"
                />
                {isCheckingUrl && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
              {errors.root_url && <p className="mt-1 text-sm text-red-600">{errors.root_url}</p>}
              <p className="mt-1 text-xs text-gray-500">Enter only the root domain (e.g. example.com) without any paths or pages</p>
            </div>
            
            <div>
              <label htmlFor="platform" className="block text-sm font-medium text-gray-700 mb-1">
                Platform *
              </label>
              <select
                id="platform"
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                disabled={isSubmitting}
                className={`${disabledStyle} border ${errors.platform ? 'border-red-500' : 'border-gray-300'} text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
              >
                {renderPlatformOptions()}
              </select>
              {errors.platform && <p className="mt-1 text-sm text-red-600">{errors.platform}</p>}
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={isSubmitting}
                rows={3}
                className={`${disabledStyle} border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                placeholder="Brief description of your website"
              />
            </div>

            <p className="text-sm text-gray-500">* Required fields</p>
          </div>
          
          <div className="mt-8 flex justify-end gap-3">
            <Link
              to="/dashboard/websites"
              className={`px-4 py-2 bg-gray-200 text-gray-800 font-medium text-sm rounded-md hover:bg-gray-300 transition-colors ${isSubmitting ? 'pointer-events-none opacity-50' : ''}`}
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting || isCheckingUrl}
              className="px-6 py-2 bg-blue-600 text-white font-medium text-sm rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {getSubmitButtonText()}
                </>
              ) : "Add Website"}
            </button>
          </div>
        </form>

        {pageSpeedAnalysis.isRunning && (
          <div className="p-5 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col">
              <p className="text-sm font-medium">{pageSpeedAnalysis.message}</p>
              <div className="h-2 w-full bg-gray-200 rounded-full mt-2">
                <div 
                  className="h-2 bg-blue-600 rounded-full" 
                  style={{ width: `${pageSpeedAnalysis.progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {pageSpeedAnalysis.status === 'starting' ? 'Starting analysis...' :
                 pageSpeedAnalysis.status === 'analyzing' ? 'Analyzing website performance...' :
                 pageSpeedAnalysis.status === 'analyzing-desktop' ? 'Analyzing desktop performance...' :
                 pageSpeedAnalysis.status === 'analyzing-mobile' ? 'Analyzing mobile performance...' :
                 pageSpeedAnalysis.status === 'complete' ? 'Analysis complete!' :
                 'Processing...'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddWebsite;